import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ArrowClockwiseIcon,
  PlusIcon,
  ReceiptIcon,
} from '@phosphor-icons/react';
import {
  paymentService,
  type Payment,
  type PaymentStatus,
} from '../../../shared/utils';
import { PaymentRow } from '../components/PaymentRow';
import { Breadcrumb } from '../components/Breadcrumb';
import './MyPaymentsPage.css';

const STATUS_FILTERS: Array<{ value: PaymentStatus | 'ALL'; key: string }> = [
  { value: 'ALL', key: 'dashboard.allStatuses' },
  { value: 'EXECUTED', key: 'dashboard.executed' },
  { value: 'AUTHORIZED', key: 'dashboard.authorized' },
  { value: 'PENDING', key: 'dashboard.pending' },
  { value: 'CREATED', key: 'dashboard.created' },
  { value: 'FAILED', key: 'dashboard.failed' },
  { value: 'CANCELLED', key: 'dashboard.cancelled' },
];

export const MyPaymentsPage = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'ALL'>(
    'ALL'
  );

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await paymentService.getPayments({
        status: statusFilter,
      });

      if (response.success) {
        setPayments(response.data || []);
      } else {
        setError(t('myPaymentsPage.unableToLoad'));
      }
    } catch (err) {
      const error = err as { message?: string };
      console.error('Error fetching payments:', err);
      setError(error.message || t('myPaymentsPage.unableToLoad'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const isFiltered = statusFilter !== 'ALL';

  return (
    <>
      <Breadcrumb items={[{ label: t('app.nav.payments') }]} />

      <div className="mc-heading">
        <div>
          <h1>{t('app.nav.payments')}</h1>
          <p>{t('myPaymentsPage.headingSub')}</p>
        </div>
        <Link to="/pay-rent" className="mc-btn mc-btn--primary">
          <PlusIcon size={18} weight="bold" />
          {t('app.nav.payRent')}
        </Link>
      </div>

      <section className="mc-card">
        <div className="my-payments__filters">
          <label className="mc-field">
            {t('myPaymentsPage.status')}
            <select
              value={statusFilter}
              onChange={e =>
                setStatusFilter(e.target.value as PaymentStatus | 'ALL')
              }
            >
              {STATUS_FILTERS.map(option => (
                <option key={option.value} value={option.value}>
                  {t(option.key)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="mc-btn mc-btn--soft"
            onClick={fetchPayments}
            disabled={loading}
          >
            <ArrowClockwiseIcon
              size={18}
              className={loading ? 'mc-spin' : undefined}
            />
            {t('myPaymentsPage.refresh')}
          </button>
        </div>
      </section>

      <section className="mc-card">
        {loading ? (
          <div className="mc-empty">{t('myPaymentsPage.loading')}</div>
        ) : error ? (
          <div className="mc-callout mc-callout--error">
            <strong>{t('myPaymentsPage.unableToLoad')}</strong>
            <p>{error}</p>
            <button
              type="button"
              className="mc-btn mc-btn--outline mc-btn--small my-payments__retry"
              onClick={fetchPayments}
            >
              {t('myPaymentsPage.retry')}
            </button>
          </div>
        ) : payments.length === 0 ? (
          isFiltered ? (
            <div className="mc-empty">{t('dashboard.noPayments')}</div>
          ) : (
            <div className="my-payments__empty">
              <span className="mc-metric__symbol">
                <ReceiptIcon size={27} weight="duotone" />
              </span>
              <h2>{t('myPaymentsPage.noPaymentsTitle')}</h2>
              <p className="mc-muted">
                {t('myPaymentsPage.noPaymentsMessage')}
              </p>
              <Link to="/pay-rent" className="mc-btn mc-btn--primary">
                {t('myPaymentsPage.payRentNow')}
              </Link>
            </div>
          )
        ) : (
          <div className="mc-tablewrap">
            <table className="mc-table">
              <thead>
                <tr>
                  <th>{t('app.table.date')}</th>
                  <th>{t('app.table.recipient')}</th>
                  <th>{t('app.table.amount')}</th>
                  <th>{t('app.table.reference')}</th>
                  <th>{t('app.table.status')}</th>
                  <th>{t('app.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <PaymentRow key={payment.id} payment={payment} showActions />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
